import { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Card } from "primereact/card";
import useInstitutionPicks from "api/BookResource/useInstitutionPicks";
import { CustomPaginator } from "components";
import Admin2 from "layouts/Admin2";
import { BookFilterProvider } from "admin/AllBooks2/BookFilter/BookFilterContext";
import useGenres from "api/GenreResource/useGenres";
import useFetchCategories from "api/CategoryResource/useFetchCategories";
import useFetchPublishers from "api/PublisherResource/useFetchPublishers";
import AdminPageLoader from "components/AdminPageLoader";
import ClearFilters from "admin/Filters/ClearFilters";
import Search from "admin/Filters/Search";
import { formData } from "admin/AllBooks2/BookFilter/booksQueryParams";
import RowsPerPage from "admin/Filters/RowsPerPage";
import useBooksFilters from "admin/AllBooks2/BookFilter/useBooksFilters";
import BookFilter from "admin/AllBooks2/BookFilter";
import BookCoverPreview from "components/BookCoverPreview";
import AssignSubjectGroups from "../InstitutionBooks/AssignSubjectGroups";
import AssignUsers from "../InstitutionBooks/AssignUsers";
import RevokeUsers from "../InstitutionBooks/RevokeUsers";
import RevokeSubjectGroups from "../InstitutionBooks/RevokeSubjectGroups";
import RestoreAccess from "../InstitutionBooks/RestoreAccess";
import { Link } from "react-router-dom";
import { activeCurrencies } from "utils";

const InstitutionPicks = () => {
	const filters = useBooksFilters();
	const [selectedBooks, setSelectedBooks] = useState([]);
	const [expandedRows, setExpandedRows] = useState(null);

	const numberOfActiveFilters = filters?.allSearchParams
		?.filter((param) => !filters?.paginationParams?.includes(param))
		?.filter((param) => filters[param] !== "")?.length;

	const { data: genres, isLoading: isLoadingGenres } = useGenres("yes");
	const { data: categories, isLoading: isLoadingCategories } = useFetchCategories("yes");
	const { data: publishers, isLoading: isLoadingPublishers } = useFetchPublishers();

	const { data: books, isLoading: isLoadingBooks } = useInstitutionPicks({
		page: filters.page,
		rows_per_page: filters.rowsPerPage,
		"filter[category_is]": filters.categoryIs,
		"filter[genre_is]": filters.genreIs,
		"filter[publisher_is]": filters.publisherIs,
		searchTerm: filters.search,
	});

	const totalRecords = books?.data?.total_records || 0;
	const booksList = books?.data?.records || [];

	const handleSelectionChange = (e) => {
		setSelectedBooks(e.value);
	};

	// Column Templates
	const bookCoverTemplate = (rowData) => {
		return (
			<BookCoverPreview
				coverUrl={rowData?.book_cover?.url}
				title={rowData?.title}
				thumbnailWidth={64}
				thumbnailHeight={80}
			/>
		);
	};

	const titleTemplate = (rowData) => {
		return (
			<div className="flex flex-col">
				<span className="font-semibold text-gray-900">{rowData?.title}</span>
				{rowData?.isbn && <span className="text-xs text-gray-400">ISBN: {rowData?.isbn}</span>}
			</div>
		);
	};

	const authorTemplate = (rowData) => {
		return (
			<div className="flex flex-col">
				<span className="text-sm text-gray-700">{rowData?.author}</span>
				{rowData?.publisher && (
					<span className="text-xs text-gray-400 truncate max-w-[150px]">
						{rowData?.publisher}
					</span>
				)}
			</div>
		);
	};

	const categoryTemplate = (rowData) => {
		return <span className="text-sm text-gray-600">{rowData?.category?.name || "N/A"}</span>;
	};

	const copiesPurchasedTemplate = (rowData) => {
		return (
			<div className="text-center">
				<span className="font-bold text-gray-800 text-lg">
					{rowData?.number_of_copies_purchased || 0}
				</span>
			</div>
		);
	};

	const copiesAssignableTemplate = (rowData) => {
		const available = rowData?.number_assignable ?? 0;
		return (
			<div className="text-center">
				<span
					className={`font-bold text-lg ${available > 0 ? "text-akgreener" : "text-gray-400"}`}
				>
					{available}
				</span>
			</div>
		);
	};

	const copiesSubscribedTemplate = (rowData) => {
		return (
			<div className="flex flex-col items-center gap-1">
				<div className="text-center">
					<span className="font-bold text-gray-800">{rowData?.copies_subscribed || 0}</span>
					<span className="text-xs text-gray-500 ml-1">capacity</span>
				</div>
				<div className="text-center">
					<span className="font-bold text-orangelib">
						{rowData?.subscription_seats_assigned || 0}
					</span>
					<span className="text-xs text-gray-500 ml-1">allocated</span>
				</div>
			</div>
		);
	};

	const statusTemplate = (rowData) => {
		return (
			<Tag
				value={rowData?.is_active ? "Active" : "Inactive"}
				severity={rowData?.is_active ? "success" : "warning"}
				className="text-xs"
			/>
		);
	};

	// Row expansion template
	const rowExpansionTemplate = (data) => {
		return (
			<div className="p-4 bg-gray-50 border-t border-gray-200">
				<Card className="shadow-none border-0">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
						{/* Book Details */}
						<div>
							<h4 className="font-semibold text-lg mb-3 text-gray-800">Book Details</h4>
							<div className="space-y-2 text-sm">
								<div>
									<span className="font-medium text-gray-500">ISBN:</span>{" "}
									<span className="text-gray-800">{data?.isbn || "N/A"}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Publisher:</span>{" "}
									<span className="text-gray-800">{data?.publisher || "N/A"}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Category:</span>{" "}
									<span className="text-gray-800">{data?.category?.name || "N/A"}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Genre:</span>{" "}
									<span className="text-gray-800">{data?.genres?.[0]?.name || "N/A"}</span>
								</div>
							</div>
						</div>

						{/* Pricing */}
						<div>
							<h4 className="font-semibold text-lg mb-3 text-gray-800">Pricing</h4>
							<div className="space-y-2 text-sm">
								<div>
									<span className="font-medium text-gray-500">
										Price ({activeCurrencies.USD.label}):
									</span>{" "}
									<span className="text-gray-800">${data?.price || 0}</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">
										Price ({activeCurrencies.ZWL.label}):
									</span>{" "}
									<span className="text-gray-800">${data?.zwl_price || 0}</span>
								</div>
								<div className="flex items-center gap-2">
									<span className="font-medium text-gray-500">Free:</span>
									<Tag
										value={data?.isFree ? "Yes" : "No"}
										severity={data?.isFree ? "success" : "secondary"}
									/>
								</div>
							</div>
						</div>

						{/* Copies Summary */}
						<div>
							<h4 className="font-semibold text-lg mb-3 text-gray-800">Copies Summary</h4>
							<div className="space-y-2 text-sm">
								<div>
									<span className="font-medium text-gray-500">Copies Purchased:</span>{" "}
									<span className="text-gray-800 font-bold">
										{data?.number_of_copies_purchased || 0}
									</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Seats Remaining:</span>{" "}
									<span className="text-akgreener font-bold">
										{data?.number_assignable ?? 0}
									</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Seat Capacity:</span>{" "}
									<span className="text-gray-800 font-bold">
										{data?.copies_subscribed || 0}
									</span>
								</div>
								<div>
									<span className="font-medium text-gray-500">Students Allocated:</span>{" "}
									<span className="text-orangelib font-bold">
										{data?.subscription_seats_assigned || 0}
									</span>
								</div>
							</div>
						</div>
					</div>

					{/* Revoke Actions */}
					<div className="mt-6 pt-4 border-t border-gray-200">
						<h4 className="font-semibold text-lg mb-3 text-gray-800">Revoke Access</h4>
						<div className="flex flex-wrap items-center gap-3">
							<RevokeUsers
								book={data}
								className="py-1 px-3 gap-2 h-10 bg-red-600 text-white rounded-lg font-semibold text-sm hover:bg-red-700 transition-colors"
							>
								Revoke from students
							</RevokeUsers>
							<RevokeSubjectGroups
								book={data}
								className="py-1 px-3 gap-2 h-10 border border-red-500 text-red-600 rounded-lg font-semibold text-sm hover:bg-red-50 transition-colors"
							>
								Revoke from subject groups
							</RevokeSubjectGroups>
							<RestoreAccess
								book={data}
								className="py-1 px-3 gap-2 h-10 border border-green-500 text-green-600 rounded-lg font-semibold text-sm hover:bg-green-50 transition-colors"
							>
								Restore access
							</RestoreAccess>
						</div>
					</div>

					{/* Description */}
					{data?.description && (
						<div className="mt-6 pt-4 border-t border-gray-200">
							<h4 className="font-semibold text-lg mb-3 text-gray-800">Description</h4>
							<div
								className="text-sm text-gray-700 prose prose-sm max-w-none"
								dangerouslySetInnerHTML={{
									__html: data?.description || "No description available",
								}}
							/>
						</div>
					)}
				</Card>
			</div>
		);
	};

	if (isLoadingGenres || isLoadingCategories || isLoadingPublishers || isLoadingBooks) {
		return <AdminPageLoader />;
	}

	return (
		<Admin2 fullWidth>
			<BookFilterProvider>
				<div className="my-4 space-y-4 w-full">
					{/* Header */}
					<div className="flex items-center justify-between">
						<h3 className="font-bold flex items-center gap-3">
							<span className="text-xl">Institution Picks Bank</span>
							<span className="block bg-gray-400 rounded-full text-white py-1 px-4 text-sm">
								{totalRecords || 0}
							</span>
						</h3>
					</div>

					{/* Filters */}
					<div className="flex items-center gap-x-2 gap-y-3">
						<RowsPerPage filters={filters} formData={formData} />
						<Search filters={filters} formData={formData} />
						<ClearFilters numberOfActiveFilters={numberOfActiveFilters} filters={filters} />
						<BookFilter
							genres={genres}
							categories={categories}
							publishers={publishers}
							numberOfActiveFilters={numberOfActiveFilters}
						/>
					</div>

					{/* Bulk Actions */}
					{selectedBooks.length > 0 && (
						<div className="flex items-center gap-4 bg-akgreen/5 p-3 rounded-xl border border-akgreen/20">
							<span className="text-sm font-bold text-akgreener">
								{selectedBooks.length} book(s) selected
							</span>
							<AssignUsers
								books={selectedBooks}
								className="py-1 px-3 gap-2 h-10 bg-akgreener text-white rounded-lg font-semibold text-sm hover:bg-akgreenest transition-colors"
							>
								Allocate to students
							</AssignUsers>
							<AssignSubjectGroups
								books={selectedBooks}
								className="py-1 px-3 gap-2 h-10 border border-akgreener text-akgreener rounded-lg font-semibold text-sm hover:bg-akgreen/10 transition-colors"
							>
								Allocate to subject groups
							</AssignSubjectGroups>
						</div>
					)}

					{/* DataTable */}
					<div className="shadow-sm">
						<DataTable
							value={booksList}
							selection={selectedBooks}
							onSelectionChange={handleSelectionChange}
							selectionMode="checkbox"
							expandedRows={expandedRows}
							onRowToggle={(e) => setExpandedRows(e.data)}
							rowExpansionTemplate={rowExpansionTemplate}
							dataKey="id"
							tableClassName="font-sans text-sm"
							loading={isLoadingBooks}
							emptyMessage="No books found in picks"
							responsiveLayout="scroll"
						>
							<Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
							<Column expander style={{ width: "3rem" }} />
							<Column
								header="Cover"
								body={bookCoverTemplate}
								style={{ width: "80px" }}
								className="text-center"
							/>
							<Column
								header="Title"
								body={titleTemplate}
								style={{ minWidth: "180px" }}
								sortable
								sortField="title"
							/>
							<Column header="Author" body={authorTemplate} style={{ minWidth: "140px" }} />
							<Column header="Category" body={categoryTemplate} style={{ width: "110px" }} />
							<Column
								header="Purchased"
								body={copiesPurchasedTemplate}
								style={{ width: "100px" }}
								className="text-center"
							/>
							<Column
								header="Seats Remaining"
								body={copiesAssignableTemplate}
								style={{ width: "100px" }}
								className="text-center"
							/>
							<Column
								header="Capacity / Allocated"
								body={copiesSubscribedTemplate}
								style={{ width: "110px" }}
								className="text-center"
							/>
							<Column
								header="Status"
								body={statusTemplate}
								style={{ width: "90px" }}
								className="text-center"
							/>
						</DataTable>

						{/* Pagination */}
						<div className="flex justify-center mt-6">
							<CustomPaginator
								firstPage={filters?.firstPage}
								rowsPerPage={filters?.rowsPerPage}
								setFirstPage={(val) => filters?.changeFilter("firstPage", val)}
								setRowsPerPage={(val) => filters?.changeFilter("rowsPerPage", val)}
								totalRecords={totalRecords}
								setPage={(val) => filters?.changeFilter("page", val)}
								showRowsPerPageDropdown={false}
							/>
						</div>
					</div>

					{/* Footer */}
					<h4 className="my-4 flex justify-center items-center gap-1 text-center">
						<span className="text-3xl font-bold">{totalRecords}</span> book titles currently
						in your institution picks bank
					</h4>

					<div className="flex justify-center">
						<Link to="/books" className="text-blue-700 hover:underline">
							Want to add more books to the picks bank?
						</Link>
					</div>
				</div>
			</BookFilterProvider>
		</Admin2>
	);
};

export default InstitutionPicks;
